import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, Plus, Pencil, Trash2, Wrench, AlertTriangle, 
  FileText, CreditCard, Settings, X, Euro, Filter, Clock, ArrowRight
} from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useTranslation } from "../contexts/I18nContext";
import { ManutenzioneFormDialog, TipoInterventoDialog, ManutenzioneDeleteDialog, LinkContabilitaDialog, CollegaFatturaDialog } from "./ManutenzioniDialogs";

export default function AutomezziManutenzioniPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditAutomezzi = canEdit("automezzi");
  
  const [manutenzioni, setManutenzioni] = useState([]);
  const [automezzi, setAutomezzi] = useState([]);
  const [tipiIntervento, setTipiIntervento] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [allocazioniFatture, setAllocazioniFatture] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [linkDialog, setLinkDialog] = useState({ open: false, item: null, type: null });
  
  // Dialog collegamento fattura a manutenzione
  const [collegaFatturaDialog, setCollegaFatturaDialog] = useState({ open: false, allocazione: null });
  const [selectedManutenzioni, setSelectedManutenzioni] = useState([]);
  
  // Dialog Anagrafica Tipi Intervento
  const [tipoDialog, setTipoDialog] = useState({ open: false, editing: null });
  const [tipoForm, setTipoForm] = useState({ nome: "", descrizione: "" });
  
  // Filtro tipo intervento
  const [filterTipoIntervento, setFilterTipoIntervento] = useState("");
  
  // Form manutenzione
  const [form, setForm] = useState({
    automezzo_id: "",
    data: new Date().toISOString().split('T')[0],
    km_attuali: "",
    officina: "",
    data_prossima: "",
    km_prossimo_intervento: "",
    note: "",
    righe: [{ tipo_intervento_id: "", descrizione: "", importo: "" }]
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [manRes, autoRes, tipiRes, fattRes, movRes, allocFattRes] = await Promise.all([
        api.get(`${prefix}/automezzi/manutenzioni/lista`),
        api.get(`${prefix}/automezzi`),
        api.get(`${prefix}/tipi-intervento-manutenzione`),
        api.get(`${prefix}/automezzi/contabilita/fatture`).catch(() => ({ data: [] })),
        api.get(`${prefix}/automezzi/contabilita/movimenti-bancari`).catch(() => ({ data: [] })),
        api.get(`${prefix}/allocazioni-manutenzioni-fatture`).catch(() => ({ data: [] }))
      ]);
      setManutenzioni(manRes.data);
      setAutomezzi(autoRes.data);
      setTipiIntervento(tipiRes.data || []);
      setFatture(fattRes.data || []);
      setMovimenti(movRes.data || []);
      setAllocazioniFatture(allocFattRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({
      automezzo_id: "",
      data: new Date().toISOString().split('T')[0],
      km_attuali: "",
      officina: "",
      data_prossima: "",
      km_prossimo_intervento: "",
      note: "",
      righe: [{ tipo_intervento_id: "", descrizione: "", importo: "", data_prossima: "", km_prossimo_intervento: "" }]
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    // Prepara righe dal backend
    const righe = item.righe?.length > 0 
      ? item.righe.map(r => ({
          tipo_intervento_id: r.tipo_intervento_id || "",
          descrizione: r.descrizione || "",
          importo: r.importo ? String(r.importo) : "",
          data_prossima: r.data_prossima || "",
          km_prossimo_intervento: r.km_prossimo_intervento ? String(r.km_prossimo_intervento) : ""
        }))
      : [{ tipo_intervento_id: "", descrizione: item.descrizione || "", importo: item.importo ? String(item.importo) : "", data_prossima: "", km_prossimo_intervento: "" }];
    
    setForm({
      automezzo_id: item.automezzo_id || "",
      data: item.data || "",
      km_attuali: item.km_attuali ? String(item.km_attuali) : "",
      officina: item.officina || "",
      data_prossima: item.data_prossima || "",
      km_prossimo_intervento: item.km_prossimo_intervento ? String(item.km_prossimo_intervento) : "",
      note: item.note || "",
      righe
    });
    setDialogOpen(true);
  };

  // Gestione righe
  const addRiga = () => {
    setForm({
      ...form,
      righe: [...form.righe, { tipo_intervento_id: "", descrizione: "", importo: "", data_prossima: "", km_prossimo_intervento: "" }]
    });
  };

  const removeRiga = (index) => {
    if (form.righe.length <= 1) return;
    setForm({
      ...form,
      righe: form.righe.filter((_, i) => i !== index)
    });
  };

  const updateRiga = (index, field, value) => {
    const newRighe = [...form.righe];
    newRighe[index] = { ...newRighe[index], [field]: value };
    
    // Se si seleziona un tipo intervento, autocompila la descrizione
    if (field === 'tipo_intervento_id' && value) {
      const tipo = tipiIntervento.find(t => t.id === value);
      if (tipo && !newRighe[index].descrizione) {
        newRighe[index].descrizione = tipo.nome;
      }
    }
    
    setForm({ ...form, righe: newRighe });
  };

  // Calcola importo totale
  const getImportoTotale = () => {
    return form.righe.reduce((sum, r) => sum + (parseFloat(r.importo) || 0), 0);
  };

  const handleSubmit = async () => {
    if (!form.automezzo_id || !form.data) {
      handleApiError({ message: t("errors.requiredVehicleDate") });
      return;
    }
    
    if (form.righe.every(r => !r.descrizione)) {
      handleApiError({ message: t("errors.requiredDescriptionRow") });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        automezzo_id: form.automezzo_id,
        data: form.data,
        km_attuali: form.km_attuali ? parseInt(form.km_attuali) : null,
        officina: form.officina || null,
        data_prossima: form.data_prossima || null,
        km_prossimo_intervento: form.km_prossimo_intervento ? parseInt(form.km_prossimo_intervento) : null,
        note: form.note || null,
        righe: form.righe
          .filter(r => r.descrizione)
          .map(r => ({
            tipo_intervento_id: r.tipo_intervento_id || null,
            descrizione: r.descrizione,
            importo: parseFloat(r.importo) || 0,
            data_prossima: r.data_prossima || null,
            km_prossimo_intervento: r.km_prossimo_intervento ? parseInt(r.km_prossimo_intervento) : null
          }))
      };
      
      const prefix = getApiPrefix();
      if (editingId) {
        await api.put(`${prefix}/automezzi/manutenzioni/${editingId}`, payload);
        showSuccess(t("success.maintenanceUpdated"));
      } else {
        await api.post(`${prefix}/automezzi/manutenzioni`, payload);
        showSuccess(t("success.maintenanceRegistered"));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`${getApiPrefix()}/automezzi/manutenzioni/${deleteDialog.item.id}`);
      showSuccess(t("success.maintenanceDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    const prefix = getApiPrefix();
    for (const id of ids) {
      try {
        await api.delete(`${prefix}/automezzi/manutenzioni/${id}`);
        deleted++;
      } catch (err) {
        
      }
    }
    showSuccess(`${deleted} manutenzioni eliminate`);
    fetchData();
  };

  // Tipi Intervento CRUD
  const handleSaveTipo = async () => {
    if (!tipoForm.nome) {
      handleApiError({ message: t("errors.requiredName") });
      return;
    }
    try {
      const prefix = getApiPrefix();
      if (tipoDialog.editing) {
        await api.put(`${prefix}/tipi-intervento-manutenzione/${tipoDialog.editing.id}`, tipoForm);
        showSuccess(t("success.interventionTypeUpdated"));
      } else {
        await api.post(`${prefix}/tipi-intervento-manutenzione`, tipoForm);
        showSuccess(t("success.interventionTypeCreated"));
      }
      setTipoDialog({ open: false, editing: null });
      setTipoForm({ nome: "", descrizione: "" });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleDeleteTipo = async (tipo) => {
    if (!confirm(`Eliminare il tipo "${tipo.nome}"?`)) return;
    try {
      await api.delete(`${getApiPrefix()}/tipi-intervento-manutenzione/${tipo.id}`);
      showSuccess(t("success.interventionTypeDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  // Link fattura/movimento
  const handleLinkFattura = async (fatturaId) => {
    try {
      const idToSend = fatturaId === "_none" ? "" : fatturaId;
      await api.post(`${getApiPrefix()}/automezzi/manutenzioni/${linkDialog.item.id}/collega-fattura?fattura_id=${idToSend}`);
      showSuccess(idToSend ? "Fattura collegata" : "Fattura scollegata");
      setLinkDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorLink"));
    }
  };

  const handleLinkMovimento = async (movimentoId) => {
    try {
      await api.post(`${getApiPrefix()}/automezzi/manutenzioni/${linkDialog.item.id}/collega-movimento?movimento_bancario_id=${movimentoId || ''}`);
      showSuccess(movimentoId ? "Movimento collegato" : "Movimento scollegato");
      setLinkDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorLink"));
    }
  };

  // Collega fattura allocata a manutenzioni
  const handleCollegaFattura = async () => {
    if (selectedManutenzioni.length === 0) {
      handleApiError({ message: t("errors.selectAtLeastOneMaintenance") });
      return;
    }
    try {
      await api.post(`${getApiPrefix()}/allocazioni-manutenzioni-fatture/${collegaFatturaDialog.allocazione.id}/collega`, {
        manutenzione_ids: selectedManutenzioni
      });
      showSuccess(`Fattura collegata a ${selectedManutenzioni.length} manutenzione/i`);
      setCollegaFatturaDialog({ open: false, allocazione: null });
      setSelectedManutenzioni([]);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorLink"));
    }
  };

  const toggleManutenzione = (id) => {
    setSelectedManutenzioni(prev => 
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  // Filtra manutenzioni per tipo intervento
  const getFilteredManutenzioni = () => {
    if (!filterTipoIntervento) return manutenzioni;
    return manutenzioni.filter(m => 
      m.righe?.some(r => r.tipo_intervento_id === filterTipoIntervento)
    );
  };

  const filteredManutenzioni = getFilteredManutenzioni();

  // Stats
  const totale = manutenzioni.length;
  const conScadenze = manutenzioni.filter(m => m.alert).length;
  const costoTotale = manutenzioni.reduce((sum, m) => sum + (m.importo_totale || m.importo || 0), 0);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

  // Colonne tabella
  const columns = [
    {
      key: 'veicolo',
      label: 'Veicolo',
      getValue: (row) => `${row.marca} ${row.modello}`,
      render: (value, row) => (
        <div>
          <span className="font-medium">{value}</span>
          <span className="text-xs text-muted-foreground ml-2 font-mono">{row.targa}</span>
        </div>
      )
    },
    {
      key: 'data',
      label: t('common.date'),
      render: (value) => value || '-'
    },
    {
      key: 'descrizione_righe',
      label: 'Interventi',
      render: (value, row) => (
        <div className="max-w-[300px]">
          {row.righe?.length > 0 ? (
            <div className="space-y-0.5">
              {row.righe.slice(0, 3).map((r, i) => (
                <div key={`riga-${r.descrizione}-${i}`} className="text-sm flex items-center gap-2">
                  <span className="truncate">{r.descrizione}</span>
                  {r.importo > 0 && (
                    <Badge variant="outline" className="text-xs shrink-0">
                      {formatCurrency(r.importo)}
                    </Badge>
                  )}
                </div>
              ))}
              {row.righe.length > 3 && (
                <span className="text-xs text-muted-foreground">+{row.righe.length - 3} altri</span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">{value || '-'}</span>
          )}
        </div>
      )
    },
    {
      key: 'importo_totale',
      label: t('common.amount'),
      align: 'right',
      render: (value, row) => (
        <span className="font-mono font-medium">
          {formatCurrency(value || row.importo)}
        </span>
      )
    },
    {
      key: 'costo_imponibile',
      label: 'Imponibile',
      align: 'right',
      render: (value) => (
        <span className="font-mono font-medium text-blue-600">
          {value ? formatCurrency(Math.abs(value)) : '-'}
        </span>
      )
    },
    {
      key: 'costo_effettivo',
      label: 'Costo Finale',
      align: 'right',
      render: (value) => (
        <span className="font-mono font-medium text-purple-600">
          {value ? formatCurrency(Math.abs(value)) : '-'}
        </span>
      )
    },
    {
      key: 'km_attuali',
      label: 'KM',
      align: 'right',
      render: (value) => value ? value.toLocaleString('it-IT') : '-'
    },
    {
      key: 'officina',
      label: 'Officina',
      render: (value) => value || '-'
    },
    {
      key: 'data_prossima',
      label: 'Prossimo',
      render: (value, row) => {
        if (!value) return '-';
        const alertClass = row.alert === 'scaduta' ? 'text-red-600 font-medium' : 
                           row.alert === 'in_scadenza' ? 'text-orange-600' : '';
        return <span className={alertClass}>{value}</span>;
      }
    },
    {
      key: 'contabilita',
      label: 'Contab.',
      sortable: false,
      filterable: false,
      render: (_, row) => (
        <div className="flex gap-1">
          {row.fattura_id ? (
            <Badge variant="outline" className="text-xs cursor-pointer text-green-600" onClick={() => setLinkDialog({ open: true, item: row, type: 'fattura' })}>
              <FileText className="h-3 w-3 mr-1" />
              F.{row.fattura_numero}
            </Badge>
          ) : (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setLinkDialog({ open: true, item: row, type: 'fattura' })}>
              <FileText className="h-3 w-3 mr-1" />
              +Fattura
            </Button>
          )}
          {row.movimento_bancario_id ? (
            <Badge variant="outline" className="text-xs cursor-pointer text-blue-600" onClick={() => setLinkDialog({ open: true, item: row, type: 'movimento' })}>
              <CreditCard className="h-3 w-3 mr-1" />
              Mov.
            </Badge>
          ) : (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setLinkDialog({ open: true, item: row, type: 'movimento' })}>
              <CreditCard className="h-3 w-3 mr-1" />
              +Mov.
            </Button>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEditDialog(row)} title={t("common.edit")}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setLinkDialog({ open: true, item: row, type: 'fattura' })} title={t("maintenance.linkInvoice")}>
            <FileText className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, item: row })} className="text-destructive" title={t("common.delete")}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="manutenzioni-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="h-7 w-7 text-orange-500" />
            Manutenzioni Automezzi
          </h1>
          <p className="text-muted-foreground text-sm">Gestisci gli interventi di manutenzione del parco veicoli</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => { setTipoDialog({ open: true, editing: null }); setTipoForm({ nome: "", descrizione: "" }); }}
          >
            <Settings className="h-4 w-4 mr-2" />
            Tipi Intervento
          </Button>
        </div>
      </div>

      {/* Fatture in attesa di collegamento */}
      {allocazioniFatture.length > 0 && (
        <Card className="border-green-200 bg-green-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Clock className="h-5 w-5" />
              Fatture in Attesa di Collegamento ({allocazioniFatture.length})
            </CardTitle>
            <CardDescription>
              Fatture allocate come &quot;Manutenzioni Automezzi&quot; da collegare alle manutenzioni registrate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {allocazioniFatture.map(alloc => (
                <div key={alloc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        <FileText className="h-3 w-3 mr-1" />
                        {alloc.fattura_numero}
                      </Badge>
                      <span className="font-medium">
                        {formatCurrency(alloc.importo)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {alloc.fornitore} • {alloc.fattura_data}
                    </p>
                    {alloc.descrizione && (
                      <p className="text-xs text-muted-foreground">{alloc.descrizione}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setCollegaFatturaDialog({ open: true, allocazione: alloc });
                      setSelectedManutenzioni([]);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />{t('common.link')}</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Wrench className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t("maintenance.totalInterventions")}</p>
                <p className="text-2xl font-bold tracking-tight">{totale}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Con Scadenze</p>
                <p className="text-2xl font-bold text-orange-600">{conScadenze}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Euro className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('common.totalCost')}</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(costoTotale)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Riepilogo con filtro */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Riepilogo Manutenzioni</CardTitle>
              <CardDescription>Elenco di tutti gli interventi registrati</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={filterTipoIntervento || "_all"} onValueChange={(v) => setFilterTipoIntervento(v === "_all" ? "" : v)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtra per tipo..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">{t('common.allTypes')}</SelectItem>
                  {tipiIntervento.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredManutenzioni}
            onDelete={handleBulkDelete}
            idField="id"
            emptyMessage="Nessuna manutenzione registrata"
            exportFileName="manutenzioni_automezzi"
            toolbar={
              <Button onClick={openAddDialog} data-testid="add-manutenzione-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuova Manutenzione
              </Button>
            }
          />
        </CardContent>
      </Card>

      {/* Dialog Manutenzione */}
      <ManutenzioneFormDialog
        open={dialogOpen} onClose={() => setDialogOpen(false)} editingId={editingId}
        form={form} setForm={setForm} onSubmit={handleSubmit} saving={saving}
        automezzi={automezzi} tipiIntervento={tipiIntervento}
        addRiga={addRiga} updateRiga={updateRiga} removeRiga={removeRiga}
        formatCurrency={formatCurrency} getImportoTotale={getImportoTotale} t={t}
      />

      <TipoInterventoDialog
        open={tipoDialog.open} onClose={() => setTipoDialog({ ...tipoDialog, open: false })}
        tipiIntervento={tipiIntervento} tipoForm={tipoForm} setTipoForm={setTipoForm}
        editing={tipoDialog.editing} setEditing={(e) => setTipoDialog({ ...tipoDialog, editing: e })}
        onSave={handleSaveTipo} onDelete={handleDeleteTipo} t={t}
      />

      <ManutenzioneDeleteDialog
        open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}
        onDelete={handleDelete} t={t}
      />

      <LinkContabilitaDialog
        open={linkDialog.open} onClose={() => setLinkDialog({ open: false, item: null, type: null })}
        linkDialog={linkDialog} fatture={fatture} movimenti={movimenti}
        onLinkFattura={handleLinkFattura} onLinkMovimento={handleLinkMovimento}
        formatCurrency={formatCurrency} t={t}
      />

      <CollegaFatturaDialog
        open={collegaFatturaDialog.open}
        onClose={() => { setCollegaFatturaDialog({ open: false, allocazione: null }); setSelectedManutenzioni([]); }}
        allocazione={collegaFatturaDialog.allocazione}
        manutenzioni={manutenzioni} automezzi={automezzi}
        selectedManutenzioni={selectedManutenzioni} toggleManutenzione={toggleManutenzione}
        onCollega={handleCollegaFattura} formatCurrency={formatCurrency} t={t}
      />
    </div>
  );
}
